import React, { useState, useEffect, useRef } from "react";
import { Col, Form } from "react-bootstrap";
import * as resourceService from "../../services/resourceService";
import { nameWithSkill } from "../../services/skillService";
import useSkills from "../../hooks/useSkills";

// Optional bookable resource on a booking (a bay, a van, a room, a court). A
// resource is a pure asset: no login, never an employee. Everything here is
// opt-in: when the vendor has no active resources the field renders nothing
// and the parent sends no `resource_ids` key at all, so the booking form is
// exactly what it was.

// Loads the vendor's active resources once. `loaded` is false until the first
// answer; a failure (including a crew login, which the staff-only endpoint
// refuses) is the same as "no resources".
export function useActiveResources() {
  const [state, setState] = useState({ loaded: false, resources: [] });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await resourceService.getResources({ active: 1 });
        if (!cancelled) setState({ loaded: true, resources: list?.success ? list.data : [] });
      } catch (e) {
        if (!cancelled) setState({ loaded: true, resources: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

const CHECK_DEBOUNCE_MS = 350;

// props
//   resources        from useActiveResources
//   value            comma list of resource ids ("" = none); the UI edits one id
//   onChange(csv)    called with the new comma list
//   label            industry wording, chosen by the parent (inline ternaries)
//   date             'YYYY-MM-DD'
//   time             'HH:MM'
//   durationMin      wall-clock minutes (total work / crew), same rule as the server
//   offeringIds / crewCount   alternative to durationMin: send the chosen offerings and
//                    let the server apply its own duration rule (used by the clinic form)
//   colMd            column width (3 inside the booking row, 12 in a side panel)
//   excludeGroupId   the booking being edited, so it does not clash with itself
//   currentLabel     name for `value` when it is not in the active list (deactivated)
function ResourceField({
  resources,
  value,
  onChange,
  label,
  date,
  time,
  durationMin,
  offeringIds,
  crewCount,
  colMd = 3,
  excludeGroupId,
  currentLabel,
  disabled
}) {
  const [conflict, setConflict] = useState(null);
  const checkSeq = useRef(0);
  const { skills } = useSkills();

  const isMulti = String(value || "").includes(",");
  const offeringKey = (offeringIds || []).join(",");

  // Advisory clash check. The server checks again when the booking is saved,
  // so this only informs - it never blocks the Save button.
  useEffect(() => {
    if (!value || !date || !time) {
      setConflict(null);
      return undefined;
    }
    const seq = ++checkSeq.current;
    const timer = setTimeout(async () => {
      try {
        const res = await resourceService.checkResources({
          resource_ids: value,
          date,
          time,
          exclude_group_id: excludeGroupId || undefined,
          ...(offeringIds?.length
            ? { offering_ids: offeringIds, crew_count: crewCount || 1 }
            : { duration_min: durationMin })
        });
        if (seq === checkSeq.current) setConflict(res?.success && res.data?.conflict ? res.data : null);
      } catch (e) {
        if (seq === checkSeq.current) setConflict(null);
      }
    }, CHECK_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, date, time, durationMin, offeringKey, crewCount, excludeGroupId]);

  if (!resources.length && !value) return null;

  const inActiveList = resources.some((r) => String(r.id) === String(value));
  // The people mapped to the chosen resource (its pool), shown read-only: the
  // booking's own steps are where they actually get assigned.
  const chosen = isMulti ? null : resources.find((r) => String(r.id) === String(value));
  const mappedCrew = chosen?.crew || [];

  return (
    <>
      <Col md={colMd} style={{ marginBottom: "1%" }}>
        <div className="d-flex flex-column">
          <Form.Label>{label}</Form.Label>
          {isMulti ? (
            <div className="d-flex gap-2 align-items-center rounded border p-2" style={{ minHeight: "40px" }}>
              <span style={{ fontSize: "14px", flex: 1 }}>{currentLabel || "Multiple resources"}</span>
              {!disabled && (
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onChange("")}>
                  Clear
                </button>
              )}
            </div>
          ) : (
            <select
              className="form-select"
              style={{ height: "40px", fontSize: "14px", borderRadius: "6px", border: "1px solid #E3EBF8" }}
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              disabled={disabled}
            >
              <option value="">None</option>
              {value && !inActiveList && (
                <option value={value}>{currentLabel ? `${currentLabel} (inactive)` : `Resource #${value} (inactive)`}</option>
              )}
              {resources.map((r) => (
                <option key={r.id} value={String(r.id)}>
                  {r.name}
                  {r.type_name ? ` (${r.type_name})` : ""}
                </option>
              ))}
            </select>
          )}
          {mappedCrew.length > 0 && (
            <small className="text-muted" style={{ marginTop: "2px" }}>
              Crew: {mappedCrew.map((c) => nameWithSkill(c.name, c.skill_key, skills)).join(", ")}
            </small>
          )}
        </div>
      </Col>
      {conflict && (
        <Col xs={12} style={{ marginBottom: "1%" }}>
          <div className="alert alert-warning py-2 mb-0" role="alert" style={{ fontSize: "13px" }}>
            <strong>{label} already in use at this time:</strong> {resourceService.describeConflict(conflict)}. You can
            still save; it will be booked twice.
          </div>
        </Col>
      )}
    </>
  );
}

export default ResourceField;
